import type { z } from "zod";
import type { EndpointDef } from "./endpoint";
import { ApiClientError } from "./client-error";
import { API_ERROR_CODES } from "./errors";
import type { ApiContract } from "./index";
import type { RouteDef } from "./routes";
import { API_ROUTES } from "./routes.generated";

// Re-exported so existing importers keep working; code that only needs the
// error class should import "./client-error".
export { ApiClientError };

/*
 * No zod and no contract at RUNTIME in this module: the browser loads it on
 * every page that calls the API, and the full contract with its schemas is
 * ~465 KB. The types still come from the contract (`import type` is erased);
 * the request is built from the generated route table (./routes.ts), and the
 * two envelopes are checked by hand below, to the same shape as
 * `errorEnvelope` in ./envelope.ts and `{ data, meta }`.
 */
type In<E extends EndpointDef> = (E["params"] extends z.ZodTypeAny ? { params: z.input<E["params"]> } : { params?: never }) &
  (E["query"] extends z.ZodTypeAny ? { query?: z.input<E["query"]> } : { query?: never }) &
  (E["body"] extends z.ZodTypeAny ? { body: z.input<E["body"]> } : { body?: never });

type Out<E extends EndpointDef> = E["list"] extends true
  ? { items: z.infer<E["response"]>[]; next_cursor: string | null; meta: Record<string, unknown> }
  : z.infer<E["response"]>;

type Fetcher = (path: string, init: RequestInit) => Promise<Response>;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/** `{ error: { code, message, details? } }` with a known code, or null. */
export function readErrorEnvelope(
  json: unknown
): { code: keyof typeof API_ERROR_CODES; message: string; details?: Record<string, unknown> } | null {
  if (!isRecord(json) || !isRecord(json.error)) return null;
  const { code, message, details } = json.error;
  if (typeof code !== "string" || !Object.prototype.hasOwnProperty.call(API_ERROR_CODES, code)) return null;
  if (typeof message !== "string") return null;
  if (details !== undefined && !isRecord(details)) return null;
  return { code: code as keyof typeof API_ERROR_CODES, message, ...(details ? { details } : {}) };
}

/** `{ data, meta: {...} }`, or null. */
export function readResponseEnvelope(json: unknown): { data: unknown; meta: Record<string, unknown> } | null {
  if (!isRecord(json) || !("data" in json) || !isRecord(json.meta)) return null;
  return { data: json.data, meta: json.meta };
}

/**
 * Browser/SSR client for one contract map; the path is "/api<path>" so each
 * app's apiFetch resolves host + version. At runtime only `method`, `path`
 * and `list` are read from `contract`, so a route table (./routes.ts) serves
 * as well as the contract itself.
 */
export function buildClient<C extends Record<string, EndpointDef>>(
  contract: C | { readonly [K in keyof C]: RouteDef },
  fetcher: Fetcher
) {
  return {
    async request<K extends keyof C & string>(key: K, input?: In<C[K]>, init: RequestInit = {}): Promise<Out<C[K]>> {
      const e = (contract as Record<string, RouteDef>)[key];
      const params = (input as { params?: Record<string, string> } | undefined)?.params ?? {};
      const query = (input as { query?: Record<string, unknown> } | undefined)?.query ?? {};
      const body = (input as { body?: unknown } | undefined)?.body;
      // A missing param would otherwise interpolate as the literal string
      // "undefined" and silently hit the wrong URL; fail before fetching.
      const path = e.path.replace(/:([A-Za-z_]+)/g, (_m, name: string) => {
        const value = params[name];
        if (value === undefined || value === null) {
          throw new ApiClientError(0, "invalid_request", `Missing path param "${name}" for ${key}.`);
        }
        return encodeURIComponent(String(value));
      });
      const qs = new URLSearchParams();
      for (const [k, v] of Object.entries(query)) {
        if (v === undefined || v === null) continue;
        // Array values repeat the key (?tag=a&tag=b) instead of joining, so
        // the server sees the same shape a native <form> or query-string
        // parser would produce for a multi-value param.
        if (Array.isArray(v)) for (const item of v) qs.append(k, String(item));
        else qs.set(k, String(v));
      }
      // `qs.size` is newer than ES2020 in some DOM typings; the string itself
      // is always available and just as cheap to test for emptiness.
      const qsString = qs.toString();
      const url = `/api${path}${qsString ? `?${qsString}` : ""}`;

      // `Headers` normalizes whatever shape the caller passed (a `Headers`
      // instance, a plain object, or a tuple array) — spreading `init.headers`
      // into a new plain object silently drops the first two.
      const headers = new Headers(init.headers);
      if (body !== undefined && !headers.has("content-type")) {
        headers.set("content-type", "application/json");
      }

      let res: Response;
      try {
        res = await fetcher(url, {
          ...init,
          method: e.method,
          headers,
          ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
        });
      } catch (err) {
        throw new ApiClientError(0, "network_error", err instanceof Error ? err.message : "Network error.");
      }

      const json: unknown = await res.json().catch(() => null);
      if (!res.ok) {
        const error = readErrorEnvelope(json);
        if (error) throw new ApiClientError(res.status, error.code, error.message, error.details);
        throw new ApiClientError(res.status, "contract_mismatch", "The error response does not match the contract.");
      }

      const envelope = readResponseEnvelope(json);
      if (!envelope) {
        throw new ApiClientError(res.status, "contract_mismatch", "The response does not match the contract.");
      }
      if (e.list) {
        if (!Array.isArray(envelope.data)) throw new ApiClientError(res.status, "contract_mismatch", "Expected a list.");
        const meta = envelope.meta as { next_cursor?: string | null };
        return { items: envelope.data, next_cursor: meta.next_cursor ?? null, meta: envelope.meta } as Out<C[K]>;
      }
      return envelope.data as Out<C[K]>;
    },
  };
}

export function createApiClient(fetcher: Fetcher) {
  return buildClient<ApiContract & Record<string, EndpointDef>>(
    API_ROUTES as { readonly [K in keyof ApiContract]: RouteDef },
    fetcher
  );
}
